
// src/pages/Workers/Departments/Departments.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Paper,
  Pagination,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "../../../../contexts/ThemeContext";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import CustomText, { textStyles } from "../../../../components/CustomText";
import ExportDropdown from "../../../../components/ExportDropdown";
import FilterDrawer from "./FilterDrawer";
import SearchInput from "../../../../components/SearchInput";
import StatusDropdown from "../../../../components/StatusDropdown";
import AddDepartment from "./AddDrawer";
import { showUnderDevelopment } from "../../../../components/Alert.js";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption";
import { BASE_URL } from "../../../../components/BaseUrl";
import { useNavigate } from "react-router-dom";
import UpdateDepartment from "./UpdateDrawer.js";
import DeleteConfirmModal from "./DeleteModel.js";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import empty from "../../../../assets/empty.png";
import {Drawer } from '@mui/material';
import ExportDepartmentsPDF from "./DepartmentPdf.js";
import ExportDepartmentsExcel from "./DepartmentExcel.js";
import { pdf } from "@react-pdf/renderer";
import {toast} from "react-hot-toast";
import { useCompanyProfile } from "../../../../components/CompanyProfile";

const Departments = () => {
  const { t } = useTranslation();
  const { primaryColor, secondaryColor, primaryTextColor, secondaryTextColor } = useTheme();
  const navigate = useNavigate();

  const [tab, setTab] = useState(1);
  const isMobile = useMediaQuery("(max-width:768px)");
  const [openAddDepartment, setOpenAddDepartment] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [departments, setDepartments] = useState([]);

  // ✅ two loaders
  const [initialLoading, setInitialLoading] = useState(true); // big loader on very first load only
  const [tableLoading, setTableLoading] = useState(false);    // small loader on interactions

  const [errorMsg, setErrorMsg] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
const [openDrawer, setOpenDrawer] = useState(false);

  // Auth
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;

  const handleTabChange = (e, newValue) => {
    if (newValue === 2) {
      showUnderDevelopment();
      return;
    }
    setTab(newValue);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
 const [filters, setFilters] = useState({
  status: "",
  startDate: "",
  endDate: "",
});
const isFilterApplied = Boolean(filters.status || filters.startDate || filters.endDate);

  // ✅ track very first render to show big loader only once
  const firstLoadRef = useRef(true);

  // ✅ stable fetch that decides which loader to show based on the "initial" flag
  const fetchDepartments = useCallback(
    async ({ initial = false } = {}) => {
      try {
        if (initial) setInitialLoading(true);
        else setTableLoading(true);

        setErrorMsg("");

        const params = new URLSearchParams({
          page: String(page + 1),
          limit: String(pageSize),
          sortBy,
          sortOrder,
          includeInactive: "true",
        });

        if (filters.name) params.set("search", filters.name);
        else if (searchTerm) params.set("search", searchTerm);

      if (filters.startDate) params.set("startDate", filters.startDate);
if (filters.endDate) params.set("endDate", filters.endDate);
if (filters.status) params.set("status", filters.status);

        const res = await fetch(`${BASE_URL}/departments?${params.toString()}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${decryptedToken}`,
            "Content-Type": "application/json",
          },
        });

        const json = await res.json();

        if (json?.error) {
          setErrorMsg(json?.message || t("toast.departmentFetchFailed"));
          setDepartments([]);
          setTotalPages(1);
          return;
        }

        if (Array.isArray(json?.data?.departments)) {
          setDepartments(json.data.departments);
          setTotalPages(json.data.pagination?.pages || 1);
        } else {
          setDepartments([]);
          setTotalPages(1);
        }
      } catch (err) {
        console.error("Error fetching departments:", err);
        setErrorMsg(t("toast.departmentFetchNetworkError"));
        setDepartments([]);
        setTotalPages(1);
      } finally {
        if (initial) setInitialLoading(false);
        else setTableLoading(false);
      }
    },
    // ❗ do NOT include initialLoading/tableLoading in deps
    [decryptedToken, page, pageSize, searchTerm, sortBy, sortOrder, filters.name, filters.status, filters.startDate, filters.endDate]
  );

  // ✅ single effect; shows big loader only on the first run
  useEffect(() => {
    if (!decryptedToken) {
      setInitialLoading(false);
      setErrorMsg("Missing auth token.");
      return;
    }

    const isInitial = firstLoadRef.current;
    fetchDepartments({ initial: isInitial });
    if (isInitial) firstLoadRef.current = false;
  }, [
    decryptedToken,
    page,
    pageSize,
    searchTerm,
    sortBy,
    sortOrder,
    filters.name,
    filters.status,
    filters.startDate,
    filters.endDate,
    fetchDepartments,
  ]);

  const rows = Array.isArray(departments) ? departments : [];
const isEmpty = !initialLoading && !tableLoading && !errorMsg && rows.length === 0;
  const renderSortIcon = (column) => {
    if (sortBy === column) {
      return sortOrder === "asc" ? (
        <ArrowUpwardIcon sx={{ fontSize: 16 }} />
      ) : (
        <ArrowDownwardIcon sx={{ fontSize: 16 }} />
      );
    }
    return <ArrowUpwardIcon sx={{ fontSize: 16, opacity: 0.3 }} />;
  };

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuDept, setMenuDept] = useState(null);

  const handleMenuOpen = (event, dept) => {
    setMenuAnchor(event.currentTarget);
    setMenuDept(dept);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuDept(null);
  };
const handleView = () => {
  // Navigate to department detail page with department data and ID
  navigate(`/department-detail/${menuDept.id}`, { 
    state: { department: menuDept } 
  });
  handleMenuClose();
};

  const [openUpdate, setOpenUpdate] = useState(false);
  const [editDept, setEditDept] = useState(null);

  const handleEdit = () => {
    setEditDept(menuDept);
    setOpenUpdate(true);
    handleMenuClose();
  };

  const [deleteDept, setDeleteDept] = useState(null);

  // utils/fetchAllDepartments.js (or inside Departments.jsx)
const fetchAllDepartments = async (decryptedToken, searchTerm, filters, sortBy, sortOrder) => {
  const queryParams = new URLSearchParams({
    no_pagination: "true",
    sort_by: sortBy || "created_at",
    sort_order: sortOrder?.toUpperCase() || "DESC",
    includeInactive: "true",
  });

  if (searchTerm) queryParams.append("search", searchTerm);
  if (filters.status) queryParams.append("status", filters.status);
  if (filters.startDate) queryParams.append("startDate", filters.startDate);
if (filters.endDate) queryParams.append("endDate", filters.endDate);
  const url = `${BASE_URL}/departments?${queryParams.toString()}`;
  console.log("📡 Departments Export API URL:", url);

  try {
    const res = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${decryptedToken}`,
      },
    });
    const json = await res.json();

    if (json.error) {
      toast.error(json.message || t("toast.departmentFetchFailed"));
      return [];
    }
    console.log("🔍 Departments Export API Response:", json.data?.departments);

    return (json.data?.departments || []).map((d) => ({
      id: d.id,
      name: d.name,
      workerCount: d.worker_count || 0,
      status: d.is_active ? "Active" : "Inactive",
      date: d.created_at
        ? new Date(d.created_at).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "—",
    }));
  } catch (err) {
    console.error("❌ Error fetching all departments:", err);
    toast.error(t("toast.departmentFetchNetworkError"));
    return [];
  }
};

const { logoBase64, loading } = useCompanyProfile(decryptedToken);
  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: 2,
        py: 2,
        mx: 1,
        mt: 1,
        border: selectedDepartment ? "none" : "2px solid #091E4224",
        minHeight: "400px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {initialLoading ? (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      ) : errorMsg ? (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
          <Typography color="error" sx={{ fontSize: 14 }}>
            {errorMsg}
          </Typography>
        </Box>
      ) :  (
        <>
         

          {/* Header Actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: isMobile ? "wrap" : "nowrap",
              pb: 2,
              px: 2,
              pt: 1,
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CustomText
                sx={{
                  ...textStyles.h1,
                  fontWeight: 600,
                  fontSize: "18px",
                  color: "#003149",
                  textAlign: "left",
                }}
              >
                {t("departments.manageDepartments")}
              </CustomText>
              {/* ✅ small loader only for user actions */}
              {tableLoading && <CircularProgress size={20} sx={{ color: primaryColor }} />}
            </Box>

            <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
              <SearchInput
                placeholder={t("departments.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
             <ExportDropdown
  onDownloadPDF={async () => {
    const allData = await fetchAllDepartments(decryptedToken, searchTerm, filters, sortBy, sortOrder);
    const blobStream = pdf(<ExportDepartmentsPDF data={allData}  logo={logoBase64}/>).toBlob();
    blobStream.then((blob) => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Departments_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.click();
    });
  }}
  onDownloadExcel={async () => {
    const allData = await fetchAllDepartments(decryptedToken, searchTerm, filters, sortBy, sortOrder);
    ExportDepartmentsExcel(allData);
  }}
/>

              <Button
                variant="contained"
                size="small"
                onClick={() => setOpenAddDepartment(true)}
                sx={{
                  px: 2,
                  backgroundColor: primaryColor,
                  color: primaryTextColor,
                  height: 32,
                  fontSize: 14,
                  fontWeight: 400,
                  fontFamily: "Poppins, sans-serif",
                  borderRadius: "4px",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: secondaryColor,
                    color: secondaryTextColor,
                  },
                  transition: "all 0.3s ease",
                }}
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              >
                {t("common.add")}
              </Button>


              {isFilterApplied ? (
  <Box
    sx={{
      position: "relative",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 34,
      height: 34,
      borderRadius: 1,
      bgcolor: primaryColor, // dynamic background
    }}
  >
    {/* Filter Icon (dynamic text color) */}
    <FilterAltIcon sx={{ color: primaryTextColor, fontSize: 20 }} />

    {/* Cross Icon (top-right corner) */}
    <IconButton
      size="small"
      onClick={() => {
   setFilters({ status: "", startDate: "", endDate: "" });
        setPage(0);
        // fetch handled by effect; table loader will show automatically
      }}
      sx={{
        position: "absolute",
        top: -6,
        right: -6,
        bgcolor: "white",
        width: 18,
        height: 18,
        border: "1px solid #C4C4C4",
        "&:hover": { 
          bgcolor: `${secondaryColor}10`,
          borderColor: secondaryColor,
        },
        transition: "all 0.3s ease",
      }}
    >
      <CloseRoundedIcon sx={{ fontSize: 14, color: secondaryTextColor }} />
    </IconButton>
  </Box>
) : (
  <IconButton
    onClick={() => setFilterDrawerOpen(true)}
      sx={{
        border: "1px solid #C4C4C4",
        borderRadius: 1,
        width: 34,
        height: 34,
        backgroundColor: "white",
        color: "#5E5C5C",
        "&:hover": { 
          backgroundColor: `${primaryColor}10`,
          borderColor: primaryColor,
        },
        transition: "all 0.3s ease",
      }}
  >
    <FilterAltIcon />
  </IconButton>
)}

            </Box>
          </Box>

          {/* Table */}
          <TableContainer component={Paper} sx={{ boxShadow: "none" }}>

            {isEmpty ? (
  <Box
    sx={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      py: 5,
    }}
  >
    <img src={empty} alt="No Data" style={{ width: 200, marginBottom: 16 }} />
    <CustomText
      sx={{
        ...textStyles.h1,
        fontWeight: 500,
        fontSize: "16px",
        color: "#003149",
      }}
    >
     {t("empty.no_user")}
    </CustomText>
  </Box>
) : (
  <>
    {/* Table */}
    <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
      <Table>
        <TableHead
          sx={{
            borderTop: "2px solid #091E4224",
            borderBottom: "2px solid #091E4224",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          <TableRow>
            <TableCell sx={{ ...headerCell, pl: 1.5 }} onClick={() => handleSort("id")}>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                {t("common.id")}
                {renderSortIcon("id")}
              </Box>
            </TableCell>
            <TableCell sx={headerCell} onClick={() => handleSort("name")}>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                {t("common.departmentName")}
                {renderSortIcon("name")}
              </Box>
            </TableCell>
            <TableCell sx={headerCell}>{t("common.worker")}</TableCell>
            <TableCell sx={headerCell}>{t("common.status")}</TableCell>
            <TableCell sx={headerCell} onClick={() => handleSort("created_at")}>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                {t("common.date")}
                {renderSortIcon("created_at")}
              </Box>
            </TableCell>
            <TableCell sx={headerCell}>{t("common.action")}</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((dept) => (
            <TableRow key={dept?.id ?? Math.random()}>
              <TableCell sx={{ ...dataCell, pl: 1.5 }}>{dept?.id ?? "—"}</TableCell>
              <TableCell sx={dataCell}>{dept?.name ?? "—"}</TableCell>
              <TableCell sx={dataCell}>{dept?.worker_count ?? "—"}</TableCell>
              <TableCell>
                <Box sx={{ pointerEvents: "none" }}>
                  <StatusDropdown currentStatus={dept?.is_active ? "Active" : "Inactive"} />
                </Box>
              </TableCell>
              <TableCell sx={dataCell}>
                {dept?.created_at
                  ? new Date(dept.created_at).toLocaleString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : "—"}
              </TableCell>
              <TableCell>
                <IconButton onClick={(e) => handleMenuOpen(e, dept)}>
                  <MoreVertIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

    {/* Pagination */}
    <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", mt: 2 }}>
      <Pagination
        count={totalPages}
        page={page + 1}
        onChange={(e, value) => setPage(value - 1)}
        shape="rounded"
        color="primary"
        size="small"
        sx={{
          "& .MuiPaginationItem-root": {
            fontFamily: "Poppins, sans-serif",
            fontSize: 13,
            fontWeight: 500,
          },
          "& .Mui-selected": {
            backgroundColor: `${primaryColor}10`,
            color: primaryColor,
          },
        }}
      />
    </Box>
  </>
)}

   
          </TableContainer>


          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MenuItem onClick={handleView} sx={{ fontSize: "13px", fontFamily: "Poppins, sans-serif" }}>
              {t("common.view")}
            </MenuItem>
            <MenuItem onClick={handleEdit} sx={{ fontSize: "13px", fontFamily: "Poppins, sans-serif" }}>
              {t("common.edit")}
            </MenuItem>
            <MenuItem
              sx={{ fontSize: "13px", fontFamily: "Poppins, sans-serif" }}
              onClick={() => {
                if (menuDept) {
                  setDeleteDept(menuDept);
                  setDeleteOpen(true);
                }
                handleMenuClose();
              }}
            >
              {t("common.delete")}
            </MenuItem>
          </Menu>

          <AddDepartment
            open={openAddDepartment}
            onClose={() => setOpenAddDepartment(false)}
            onSuccess={async () => {
              setPage(0);
              await fetchDepartments({ initial: false });
            }}
          />

          <FilterDrawer
            open={filterDrawerOpen}
            onClose={() => setFilterDrawerOpen(false)}
            filters={filters}
            setFilters={setFilters}
            onApplyFilters={(nextFilters) => {
              // ✅ just set state; effect will fetch and show small loader
              setFilters(nextFilters);
              setPage(0);
            }}
          />

          <UpdateDepartment
            open={openUpdate}
            onClose={() => setOpenUpdate(false)}
            department={editDept}
            onSuccess={async () => {
              await fetchDepartments({ initial: false });
            }}
          />

          <DeleteConfirmModal
            open={deleteOpen}
            onClose={() => setDeleteOpen(false)}
            department={deleteDept}
            loading={deleteLoading}
            onConfirm={async () => {
              if (!deleteDept?.id) return;
              try {
                setDeleteLoading(true);
                const res = await fetch(`${BASE_URL}/departments/${deleteDept.id}`, {
                  method: "DELETE",
                  headers: {
                    Authorization: `Bearer ${decryptedToken}`,
                    "Content-Type": "application/json",
                  },
                });
                const json = await res.json();
                if (!res.ok || json?.error) {
                  toast.error(json?.message || t("toast.departmentDeleteFailed"));
                } else {
                  await fetchDepartments({ initial: false });
                  toast.success(t("toast.departmentDeleted"));
                  setDeleteOpen(false);
                  setDeleteDept(null);
                }
              } catch (err) {
                console.error("Delete failed", err);
                toast.error(t("toast.departmentDeleteNetworkError"));
              } finally {
                setDeleteLoading(false);
              }
            }}
          />
        </>
      )}
    </Box>
  );
};

const headerCell = {
  color: "#44546F",
  fontSize: "15px",
  fontFamily: "Poppins, sans-serif",
  fontWeight: 600,
  cursor: "pointer",
};

const dataCell = {
  color: "#172B4D",
  fontSize: "13px",
  fontFamily: "Poppins, sans-serif",
  fontWeight: 400,
    whiteSpace: 'nowrap', // ✅ Prevent text wrap
   
      overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const tabStyle = {
  fontSize: "15px",
  fontWeight: 500,
  fontFamily: "Poppins, sans-serif",
  textTransform: "none",
  color: "#44546F",
  minHeight: "unset",
  padding: "6px 12px",
  paddingBottom: "4px",
  mt: 1,
};

export default Departments;

















